import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import api from '@/lib/api'

export interface User {
  id: string
  email: string
  firstName: string
  lastName: string
  type: number
  profilePicture: string | null
  organization: {
    id: number
    uuid: string
    name: string
  } | null
}

interface AuthTokens {
  accessToken: string
  refreshToken: string
}

interface LoginCredentials {
  email: string
  password: string
}

interface RegisterData {
  email: string
  password: string
  firstName: string
  lastName: string
  turnstileToken?: string
}

const TOKEN_KEY = 'vte_access_token'
const REFRESH_KEY = 'vte_refresh_token'

export const useAuthStore = defineStore('auth', () => {
  const router = useRouter()

  // State
  const user = ref<User | null>(null)
  const accessToken = ref<string | null>(null)
  const refreshToken = ref<string | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const initialized = ref(false)

  // Getters
  const isAuthenticated = computed(() => !!user.value && !!accessToken.value)
  const isAdmin = computed(() => user.value?.type != null && user.value.type <= 2)
  const hasOrganization = computed(() => !!user.value?.organization)
  const fullName = computed(() => {
    if (!user.value) return ''
    return `${user.value.firstName} ${user.value.lastName}`.trim()
  })

  // Actions
  function setTokens(tokens: AuthTokens) {
    accessToken.value = tokens.accessToken
    refreshToken.value = tokens.refreshToken
    localStorage.setItem(TOKEN_KEY, tokens.accessToken)
    localStorage.setItem(REFRESH_KEY, tokens.refreshToken)
  }

  function clearTokens() {
    accessToken.value = null
    refreshToken.value = null
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(REFRESH_KEY)
  }

  async function initialize() {
    if (initialized.value) return

    const storedAccessToken = localStorage.getItem(TOKEN_KEY)
    const storedRefreshToken = localStorage.getItem(REFRESH_KEY)

    if (storedAccessToken && storedRefreshToken) {
      accessToken.value = storedAccessToken
      refreshToken.value = storedRefreshToken

      try {
        // Verify token and get user
        await fetchUser()
      } catch {
        // Token invalid, try refresh
        try {
          await refresh()
          await fetchUser()
        } catch {
          // Refresh failed, clear tokens
          clearTokens()
          user.value = null
        }
      }
    }

    initialized.value = true
  }

  async function fetchUser() {
    if (!accessToken.value) throw new Error('No access token')

    const response = await api.get('/auth/me')
    user.value = response
  }

  async function login(credentials: LoginCredentials) {
    loading.value = true
    error.value = null

    try {
      const response = await api.post('/auth/login', credentials, { skipAuth: true })

      setTokens({
        accessToken: response.accessToken,
        refreshToken: response.refreshToken
      })

      user.value = response.user

      // Check for redirect parameter (e.g., from builder SSO flow)
      const urlParams = new URLSearchParams(window.location.search)
      const redirectUrl = urlParams.get('redirect')

      if (redirectUrl) {
        // Validate redirect URL is to a trusted domain
        try {
          const url = new URL(decodeURIComponent(redirectUrl))
          if (url.hostname.endsWith('virtualtoureasy.com') || url.hostname === 'localhost') {
            // Redirect to external URL (builder, viewer, etc.)
            window.location.href = decodeURIComponent(redirectUrl)
            return response.user
          }
        } catch {
          // Invalid URL, fall through to dashboard
        }
      }

      // Navigate to dashboard (default)
      router.push('/dashboard')

      return response.user
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Login failed'
      throw e
    } finally {
      loading.value = false
    }
  }

  async function register(data: RegisterData) {
    loading.value = true
    error.value = null

    try {
      const response = await api.post('/auth/register', data)
      return response
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Registration failed'
      throw e
    } finally {
      loading.value = false
    }
  }

  async function logout() {
    try {
      await api.post('/auth/logout')
    } catch {
      // Ignore logout errors
    } finally {
      clearTokens()
      user.value = null
      // Clear module-level caches so next login doesn't see stale data
      import('@/composables/usePlan').then(m => m.clearPlanCache()).catch(() => {})
      router.push('/login')
    }
  }

  async function refresh() {
    if (!refreshToken.value) throw new Error('No refresh token')

    const response = await api.post('/auth/refresh', {
      refreshToken: refreshToken.value
    })

    setTokens({
      accessToken: response.accessToken,
      refreshToken: response.refreshToken
    })

    return response
  }

  async function forgotPassword(email: string) {
    loading.value = true
    error.value = null

    try {
      const response = await api.post('/auth/forgot-password', { email })
      return response
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Request failed'
      throw e
    } finally {
      loading.value = false
    }
  }

  async function resetPassword(token: string, password: string) {
    loading.value = true
    error.value = null

    try {
      const response = await api.post('/auth/reset-password', { token, password })
      return response
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Reset failed'
      throw e
    } finally {
      loading.value = false
    }
  }

  // OAuth helpers
  function loginWithGoogle() {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787'
    window.location.href = `${apiUrl}/auth/google`
  }

  function loginWithFacebook() {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787'
    window.location.href = `${apiUrl}/auth/facebook`
  }

  // Handle OAuth callback (tokens in URL fragment)
  async function handleOAuthCallback() {
    const hash = window.location.hash.substring(1)
    const params = new URLSearchParams(hash)

    const accessTokenParam = params.get('access_token')
    const refreshTokenParam = params.get('refresh_token')

    if (accessTokenParam && refreshTokenParam) {
      setTokens({
        accessToken: accessTokenParam,
        refreshToken: refreshTokenParam
      })

      // Clear hash from URL
      window.history.replaceState(null, '', window.location.pathname)

      // Fetch user
      await fetchUser()

      // Check for redirect in session storage (set before OAuth redirect)
      const redirectUrl = sessionStorage.getItem('vte_oauth_redirect')
      sessionStorage.removeItem('vte_oauth_redirect')

      if (redirectUrl) {
        try {
          const url = new URL(redirectUrl)
          if (url.hostname.endsWith('virtualtoureasy.com') || url.hostname === 'localhost') {
            window.location.href = redirectUrl
            return true
          }
        } catch {
          // Invalid URL, fall through to dashboard
        }
      }

      // Navigate to dashboard
      router.push('/dashboard')

      return true
    }

    return false
  }

  return {
    // State
    user,
    accessToken,
    loading,
    error,
    initialized,

    // Getters
    isAuthenticated,
    isAdmin,
    hasOrganization,
    fullName,

    // Actions
    initialize,
    login,
    register,
    logout,
    refresh,
    forgotPassword,
    resetPassword,
    loginWithGoogle,
    loginWithFacebook,
    handleOAuthCallback,
    fetchUser
  }
})
